/** @jsx jsx */
import React, {
	createContext,
	type CSSProperties,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useReducer,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import type { StrictXCSSProp } from '@atlaskit/css';
import noop from '@atlaskit/ds-lib/noop';
import MenuExpandIcon from '@atlaskit/icon/glyph/menu-expand';
import RoomMenuIcon from '@atlaskit/icon/glyph/room-menu';
import { token } from '@atlaskit/tokens';

import { PanelSplitterProvider, type ResizeBounds } from './panel-splitter';

// These are "compressed" to discourage usage.
const sideNavVar = '--n_sNvw';
const asideVar = '--n_asDw';
const panelVar = '--n_pnlW';
const bannerMountedVar = '--n_bnrM';
const topBarMountedVar = '--n_tbrM';

// We aren't using template literals here because Compiled can't compiled them in platform ATM.
const contentHeightWhenFixed = `calc(100vh - var(--n_bnrM, 0px) - var(--n_tbrM, 0px))`;
const contentInsetBlockStart = `calc(var(--n_bnrM, 0px) + var(--n_tbrM, 0px))`;

// We define variables as they can change their size depending on the viewport width. That isn't needed for
// general grid item slots because, well, they just take up the size of the grid item! In this instance since
// the UNSAFE absolutely positioned sibling isn't on the grid it needs extra layout information.
// Note: THESE ARE THE SAME VALUES AS THE LEGACY PAGE LAYOUT.
// WHEN WE ELIMINATE USAGE OF THOSE VARIABLES THESE CAN BE RENAMED TO BE HASHED.
const UNSAFE_topBarVar = '--topNavigationHeight';
const UNSAFE_bannerVar = '--bannerHeight';
const UNSAFE_sideNavLayoutVar = '--leftSidebarWidth';
const UNSAFE_asideLayoutVar = '--rightSidebarWidth';
const UNSAFE_panelLayoutVar = '--rightPanelWidth';

// The following UNSAFE variables are used to absolutely position elements that aren't a child of page layout.
// Known use cases: Legacy pages rendered inside Confluence and Jira.
export const UNSAFE_MAIN_BLOCK_START_FOR_LEGACY_PAGES_ONLY = `calc(var(${UNSAFE_bannerVar}, 0px) + var(${UNSAFE_topBarVar}, 0px))`;
export const UNSAFE_MAIN_INLINE_START_FOR_LEGACY_PAGES_ONLY = `var(${UNSAFE_sideNavLayoutVar}, 0px)`;
export const UNSAFE_MAIN_INLINE_END_FOR_LEGACY_PAGES_ONLY = `calc(var(${UNSAFE_asideLayoutVar}, 0px) + var(${UNSAFE_panelLayoutVar}, 0px))`;

const panelSplitterResizingVar = '--localPanelSplitterResizingWidth';
const sideNavCollapsedWidth = '56px';

const sideNavWidthResizeBounds: ResizeBounds = { min: '240px', max: '50vw' };

/**
 * We define the z-indexes here so each page slot can be locally layered against each other.
 * For globally defined values such as flag, modal, etc, we can continue to
 * rely on accessing them through global means.
 */
const localSlotLayers = {
	// Panel should be above banner and top bar slos.
	panel: 3,
	// Banner and top bar should be above all other slots.
	banner: 2,
	topBar: 2,
	// Side nav should be above all other content slots.
	sideNav: 1,
};

const styles = cssMap({
	defaultBannerSizes: {
		[UNSAFE_bannerVar]: '64px',
	},
	defaultTopBarSizes: {
		[UNSAFE_topBarVar]: '56px',
	},
	defaultSideNavSizes: {
		// ------ START UNSAFE STYLES ------
		// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
		// When they aren't needed anymore we can delete them wholesale.
		[UNSAFE_sideNavLayoutVar]: '0px',
		'@media (min-width: 64rem)': {
			[UNSAFE_sideNavLayoutVar]: `var(${sideNavVar})`,
		},
		// ------ END UNSAFE STYLES ------
	},
	collapsedSideNavSizes: {
		[sideNavVar]: sideNavCollapsedWidth,
		// ------ START UNSAFE STYLES ------
		// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
		// When they aren't needed anymore we can delete them wholesale.
		[UNSAFE_sideNavLayoutVar]: '0px',
		'@media (min-width: 64rem)': {
			[UNSAFE_sideNavLayoutVar]: `var(${sideNavVar})`,
		},
		// ------ END UNSAFE STYLES ------
	},
	defaultAsideSize: {
		[asideVar]: '330px',
		// ------ START UNSAFE STYLES ------
		// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
		// When they aren't needed anymore we can delete them wholesale.
		[UNSAFE_asideLayoutVar]: '0px',
		'@media (min-width: 64rem)': {
			[UNSAFE_asideLayoutVar]: `var(${asideVar})`,
		},
		// ------ END UNSAFE STYLES ------
	},
	defaultPanelSize: {
		[panelVar]: '365px',
		// ------ START UNSAFE STYLES ------
		// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
		// When they aren't needed anymore we can delete them wholesale.
		[UNSAFE_panelLayoutVar]: '0px',
		'@media (min-width: 90rem)': {
			[UNSAFE_panelLayoutVar]: `var(${panelVar})`,
		},
		// ------ END UNSAFE STYLES ------
	},
	root: {
		display: 'grid',
		minHeight: '100vh',
		gridTemplateAreas: `
            "banner"
            "top-bar"
            "main"
            "aside"
       `,
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: 'auto auto 1fr auto',
		'@media (min-width: 64rem)': {
			gridTemplateAreas: `
            "banner banner banner"
            "top-bar top-bar top-bar"
            "side-nav main aside"
       `,
			gridTemplateRows: 'auto auto 3fr',
			gridTemplateColumns: 'auto minmax(0,1fr) auto',
		},
		// Panel is only shown as a separate column on large viewports
		'@media (min-width: 90rem)': {
			gridTemplateAreas: `
                "banner banner banner banner"
                "top-bar top-bar top-bar top-bar"
                "side-nav main aside panel"
           `,
			gridTemplateRows: 'auto auto 3fr',
			gridTemplateColumns: 'auto minmax(0,1fr) auto auto',
		},
	},
	banner: {
		gridArea: 'banner',
		height: `var(${UNSAFE_bannerVar})`,
		insetBlockStart: 0,
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.banner,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& ~ *': {
			// This provides the ability for all subsequent sibling elements to know both that this element
			// has been mounted and what it's intrinsic size is. This powers the stick points when scrolling.
			[bannerMountedVar]: `var(${UNSAFE_bannerVar})`,
		},
	},
	topBar: {
		backgroundColor: token('elevation.surface'),
		borderBlockEnd: `1px solid ${token('color.border')}`,
		boxSizing: 'border-box',
		gridArea: 'top-bar',
		height: `var(${UNSAFE_topBarVar})`,
		insetBlockStart: `var(${bannerMountedVar}, 0px)`,
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.topBar,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& ~ *': {
			// This provides the ability for all subsequent sibling elements to know both that this element
			// has been mounted and what it's intrinsic size is. This powers the stick points when scrolling.
			[topBarMountedVar]: `var(${UNSAFE_topBarVar})`,
		},
	},
	sideNav: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		gridArea: 'main / aside / aside / aside',
		height: contentHeightWhenFixed,
		insetBlockStart: contentInsetBlockStart,
		position: 'sticky',
		width: '90%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.sideNav,
		'@media (min-width: 48rem)': {
			width: `var(${panelSplitterResizingVar}, var(${sideNavVar}))`,
		},
		'@media (min-width: 64rem)': {
			backgroundColor: token('elevation.surface'),
			borderInlineEnd: `1px solid ${token('color.border')}`,
			boxShadow: 'initial',
			gridArea: 'side-nav',
		},
	},
	main: {
		gridArea: 'main',
	},
	aside: {
		gridArea: 'aside',
		position: 'static',
		'@media (min-width: 64rem)': {
			width: `var(${asideVar})`,
			justifySelf: 'end',
		},
	},
	panel: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		gridArea: 'main / aside / aside / aside',
		justifySelf: 'end',
		width: `var(${panelVar})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.panel,
		'@media (min-width: 90rem)': {
			backgroundColor: token('elevation.surface'),
			borderInlineStart: `1px solid ${token('color.border')}`,
			boxShadow: 'initial',
			gridArea: 'panel',
		},
	},
	fixedContentArea: {
		insetBlockStart: contentInsetBlockStart,
		overflow: 'auto',
		position: 'sticky',
		'@media (min-width: 64rem)': {
			height: contentHeightWhenFixed,
		},
	},
	visibleForSmallViewports: {
		display: 'initial',
		'@media (min-width: 64rem)': {
			display: 'none',
		},
	},
	visibleFromSmallViewports: {
		display: 'none',
		'@media (min-width: 64rem)': {
			display: 'initial',
		},
	},
});

const DangerouslyHoistSlotSizes = createContext(false);
export const SideNavSmallViewportVisibility = createContext(false);
export const SideNavCollapsedContext = createContext(false);
const ToggleSideNavSmallViewportVisibility = createContext(noop);
const ToggleSideNavCollapsed = createContext(noop);

export function useSideNavCollapsedState() {
	return useContext(SideNavCollapsedContext);
}

export function useToggleSideNavCollapsed() {
	return useContext(ToggleSideNavCollapsed);
}

function toggleReducer(state: boolean) {
	return !state;
}

export function Root({
	children,
	xcss,
	UNSAFE_dangerouslyHoistSlotSizes = false,
}: {
	children: React.ReactNode;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	UNSAFE_dangerouslyHoistSlotSizes?: boolean;
}) {
	const [sideNavShown, toggleSideNavVisibility] = useReducer(toggleReducer, false);

	return (
		<DangerouslyHoistSlotSizes.Provider value={UNSAFE_dangerouslyHoistSlotSizes}>
			<ToggleSideNavSmallViewportVisibility.Provider value={toggleSideNavVisibility}>
				<SideNavSmallViewportVisibility.Provider value={sideNavShown}>
					<div
						css={[styles.root, styles.defaultBannerSizes, styles.defaultTopBarSizes]}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={xcss}
					>
						{children}
					</div>
				</SideNavSmallViewportVisibility.Provider>
			</ToggleSideNavSmallViewportVisibility.Provider>
		</DangerouslyHoistSlotSizes.Provider>
	);
}

/**
 * This button toggles whether the side nav is visible for small viewports or not. It is only visible on small viewports.
 *
 * Key concept: on small viewports, the side nav is either hidden (not displayed at all), or visible and (forcibly) expanded.
 * On larger viewports, the side nav can be collapsed (e.g. only icons are shown), or expanded.
 *
 * Example use case: toggling the side nav visibility from the top bar, for small viewports, when the side nav has been hidden.
 */
export function SideNavButton() {
	const toggleSideNavSmallViewportVisibility = useContext(ToggleSideNavSmallViewportVisibility);
	const isSideNavShownOnSmallViewports = useContext(SideNavSmallViewportVisibility);

	return (
		<div css={styles.visibleForSmallViewports}>
			<IconButton
				onClick={toggleSideNavSmallViewportVisibility}
				appearance="subtle"
				shape="circle"
				label={isSideNavShownOnSmallViewports ? 'Close Side Navigation' : 'Open Side Navigation'}
				isSelected={isSideNavShownOnSmallViewports}
				icon={MenuExpandIcon}
			/>
		</div>
	);
}

/**
 * This button toggles whether the side nav is collapsed or expanded. It is only visible on larger viewports.
 *
 * Key concept: on small viewports, the side nav is either hidden (not displayed at all), or visible and (forcibly) expanded.
 * On larger viewports, the side nav can be collapsed (e.g. only icons are shown), or expanded.
 *
 * Example use case: toggling the side nav collapsed state from within the side nav, for larger viewports.
 */
export function SideNavCollapseButton() {
	const toggle = useContext(ToggleSideNavCollapsed);
	const isSideNavCollapsed = useContext(SideNavCollapsedContext);

	return (
		<div css={styles.visibleFromSmallViewports}>
			<IconButton
				onClick={toggle}
				appearance="subtle"
				shape="circle"
				label={isSideNavCollapsed ? 'Expand Side Navigation' : 'Collapse Side Navigation'}
				icon={RoomMenuIcon}
			/>
		</div>
	);
}

export function Banner({
	children,
	xcss,
}: {
	children: React.ReactNode;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
}) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	useLayoutEffect(() => {
		if (!dangerouslyHoistSlotSizes) {
			return;
		}

		const classNames = `${styles.defaultBannerSizes}`.split(' ');
		document.documentElement.classList.add(...classNames);

		return () => {
			document.documentElement.classList.remove(...classNames);
		};
	}, [dangerouslyHoistSlotSizes]);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div css={styles.banner} className={xcss}>
			{children}
		</div>
	);
}

export function TopBar({
	children,
	xcss,
}: {
	children: React.ReactNode;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
}) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	useLayoutEffect(() => {
		if (!dangerouslyHoistSlotSizes) {
			return;
		}

		const classNames = `${styles.defaultTopBarSizes}`.split(' ');
		document.documentElement.classList.add(...classNames);

		return () => {
			document.documentElement.classList.remove(...classNames);
		};
	}, [dangerouslyHoistSlotSizes]);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div css={styles.topBar} className={xcss}>
			{children}
		</div>
	);
}

export function Panel({ children }: { children: React.ReactNode }) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	useLayoutEffect(() => {
		if (!dangerouslyHoistSlotSizes) {
			return;
		}

		const classNames = `${styles.defaultPanelSize}`.split(' ');
		document.documentElement.classList.add(...classNames);

		return () => {
			document.documentElement.classList.remove(...classNames);
		};
	}, [dangerouslyHoistSlotSizes]);

	return (
		<div css={[styles.panel, styles.defaultPanelSize, styles.fixedContentArea]}>{children}</div>
	);
}

export function Main({
	children,
	isFixed,
	xcss,
}: {
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	children: React.ReactNode;
	isFixed?: boolean;
}) {
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={xcss} role="main" css={[styles.main, isFixed && styles.fixedContentArea]}>
			{children}
		</div>
	);
}

/**
 * Key concept: SideNav has two visibility states, based on the viewport size.
 * On small viewports: the side nav is either hidden (not displayed at all), or visible and (forcibly) expanded.
 * On larger viewports: the side nav can be collapsed (e.g. only icons are shown), or expanded.
 */
export function SideNav({
	children,
	defaultCollapsed = false,
	label = 'main',
	defaultWidth = 320,
}: {
	children: React.ReactNode;
	defaultCollapsed?: boolean;
	label?: string;
	defaultWidth?: number;
}) {
	const [isSideNavCollapsedByUser, setSideNavCollapsedByUser] = useState(defaultCollapsed);
	const [isSideNavForciblyExpanded, setForciblyExpandSideNav] = useState(false);
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
	const isSideNavShownOnSmallViewports = useContext(SideNavSmallViewportVisibility);
	const isSideNavExpanded = isSideNavForciblyExpanded || !isSideNavCollapsedByUser;
	const panelSplitterParentRef = useRef<HTMLDivElement | null>(null);
	const [width, setWidth] = useState(defaultWidth);

	const toggleSideNavCollapsedByUser = useCallback(
		() => setSideNavCollapsedByUser((prev) => !prev),
		[],
	);

	useLayoutEffect(() => {
		const mediaQueryList = window.matchMedia('(min-width: 64rem)');

		const callback = () => {
			const isSmallViewport = !mediaQueryList.matches;

			if (isSideNavShownOnSmallViewports) {
				// Going from large → small viewport. Temporarily force the side nav to be expanded, while the user is on a small viewport.
				setForciblyExpandSideNav(isSmallViewport);

				// Going from small → large viewport. Make sure it stays expanded, because it was previously shown on the small viewport.
				if (!isSmallViewport) {
					setSideNavCollapsedByUser(false);
				}
			} else if (!isSmallViewport) {
				// Going from small → large viewport. Undo the temporarily forced side nav expansion.
				setForciblyExpandSideNav(false);
			}
		};

		callback();

		// TODO: Move to bind-event-listener
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		mediaQueryList.addEventListener('change', callback);

		return () => {
			// TODO: Move to bind-event-listener
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			mediaQueryList.removeEventListener('change', callback);
		};
	}, [isSideNavShownOnSmallViewports]);

	useLayoutEffect(() => {
		if (!dangerouslyHoistSlotSizes) {
			return;
		}

		const expandedClassNames = `${styles.defaultSideNavSizes}`.split(' ');
		const collapsedClassNames = `${styles.collapsedSideNavSizes}`.split(' ');

		if (isSideNavExpanded) {
			document.documentElement.classList.add(...expandedClassNames);
		} else {
			document.documentElement.classList.add(...collapsedClassNames);
		}

		return () => {
			document.documentElement.classList.remove(...collapsedClassNames, ...expandedClassNames);
		};
	}, [isSideNavExpanded, dangerouslyHoistSlotSizes]);

	useEffect(() => {
		if (!dangerouslyHoistSlotSizes) {
			return;
		}

		document.documentElement.style.setProperty(
			sideNavVar,
			isSideNavExpanded
				? `clamp(${sideNavWidthResizeBounds.min}, ${width}px, ${sideNavWidthResizeBounds.max})`
				: sideNavCollapsedWidth,
		);
	}, [dangerouslyHoistSlotSizes, isSideNavExpanded, width]);

	return (
		<SideNavCollapsedContext.Provider value={!isSideNavExpanded}>
			<ToggleSideNavCollapsed.Provider value={toggleSideNavCollapsedByUser}>
				<nav
					aria-label={label}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={
						{
							[sideNavVar]: isSideNavExpanded
								? `clamp(${sideNavWidthResizeBounds.min}, ${width}px, ${sideNavWidthResizeBounds.max})`
								: sideNavCollapsedWidth,
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						} as CSSProperties
					}
					ref={panelSplitterParentRef}
					css={[
						styles.sideNav,
						!isSideNavExpanded && styles.collapsedSideNavSizes,
						isSideNavExpanded && styles.defaultSideNavSizes,
						!isSideNavShownOnSmallViewports && styles.visibleFromSmallViewports,
					]}
					data-testid="navigation-system.ui.page-layout.side-nav-container"
				>
					<PanelSplitterProvider
						panelRef={panelSplitterParentRef}
						panelWidth={width}
						onCompleteResize={setWidth}
						resizeBounds={sideNavWidthResizeBounds}
						resizingCssVar={panelSplitterResizingVar}
						onClick={toggleSideNavCollapsedByUser}
						isDragDisabled={!isSideNavExpanded}
					>
						{children}
					</PanelSplitterProvider>
				</nav>
			</ToggleSideNavCollapsed.Provider>
		</SideNavCollapsedContext.Provider>
	);
}

export function Aside({
	children,
	xcss,
	isFixed,
	width,
}: {
	children: React.ReactNode;
	isFixed?: boolean;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	width?: CSSProperties['width'];
}) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	useLayoutEffect(() => {
		if (!dangerouslyHoistSlotSizes) {
			return;
		}

		const classNames = `${styles.defaultAsideSize}`.split(' ');
		document.documentElement.classList.add(...classNames);

		return () => {
			document.documentElement.classList.remove(...classNames);
		};
	}, [dangerouslyHoistSlotSizes]);

	return (
		<div
			css={[styles.aside, styles.defaultAsideSize, isFixed && styles.fixedContentArea]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={xcss}
			// It is necessary to be able to dynamically set the width in order to support existing use cases
			// within Jira. Specifically the Rovo / AI chat. This is a temporary solution until we can come up with
			// a better way to handle this in a non-dynamic way.
			style={{ width }}
		>
			{children}
		</div>
	);
}
